import { useState } from 'react'
import toast from 'react-hot-toast'
import {
  getInvitationLink,
  type InvitationRow,
  resendInvitation,
  revokeInvitation,
  updateInvitationRole,
} from '../../api/user-invitations'
import { useDialog } from '../../components/dialogs/useDialog'
import type { UserRole } from '../../types/api'

export function InvitationTableRow({
  invitation,
  orgId,
  onChange,
}: {
  invitation: InvitationRow
  orgId?: number
  onChange: () => Promise<void>
}) {
  const dialog = useDialog()
  const [busy, setBusy] = useState(false)
  const expired = invitation.expiresAt !== null && Date.parse(invitation.expiresAt) <= Date.now()
  const actionClass =
    'block w-full min-h-11 px-4 text-left text-sm hover:bg-harbor-surface-raised focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50'
  async function act(action: 'resend' | 'copy' | 'revoke' | 'role') {
    setBusy(true)
    try {
      if (action === 'resend') {
        const result = await resendInvitation(invitation.id, orgId)
        if (result.emailSent) toast.success('Invitation sent. The previous link no longer works.')
        else
          toast.error('Invitation saved, but email could not be sent. Copy the link or try again.')
      } else if (action === 'copy') {
        const { invitationUrl } = await getInvitationLink(invitation.id, orgId)
        await navigator.clipboard.writeText(invitationUrl)
        toast.success('Invitation link copied')
      } else if (action === 'revoke') {
        if (
          !(await dialog.confirm({
            title: 'Revoke invitation?',
            description: 'This person will no longer be able to use this invitation.',
            confirmLabel: 'Revoke',
            tone: 'danger',
          }))
        )
          return
        await revokeInvitation(invitation.id, orgId)
        toast.success('Invitation revoked')
      } else {
        const role = await dialog.select({
          title: 'Edit role',
          label: 'Role',
          value: invitation.role,
          options: [
            { value: 'user', label: 'User' },
            { value: 'project_manager', label: 'Project Manager' },
            { value: 'admin', label: 'Admin' },
          ],
        })
        if (!role) return
        await updateInvitationRole(invitation.id, role as UserRole, orgId)
        toast.success('Role updated')
      }
      await onChange()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to update invitation')
    } finally {
      setBusy(false)
    }
  }
  return (
    <tr>
      <td className="px-4 py-4">
        <p className="font-medium text-harbor-text">
          {invitation.username ?? invitation.githubUsername ?? invitation.email}
        </p>
        <p className="text-sm text-harbor-text-secondary">
          {invitation.email ?? 'Email available after first sign-in'}
        </p>
        {invitation.deliveryStatus === 'failed' && (
          <p className="text-xs text-amber-500 mt-1">Email not sent. Resend or copy the link.</p>
        )}
      </td>
      <td className="px-4 py-4 text-sm text-harbor-text">
        {invitation.role === 'project_manager'
          ? 'Project Manager'
          : invitation.role === 'admin'
            ? 'Admin'
            : 'User'}
      </td>
      <td className="px-4 py-4">
        <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-medium bg-amber-500/15 text-amber-600 dark:text-amber-300">
          {expired ? 'Expired' : 'Invited'}
        </span>
      </td>
      <td className="px-4 py-4 text-sm text-harbor-text-secondary">Not yet signed in</td>
      <td className="px-4 py-4 text-right">
        <details className="relative inline-block text-left">
          <summary
            aria-label={`Actions for ${invitation.email ?? invitation.githubUsername}`}
            className="min-h-11 min-w-11 cursor-pointer rounded-lg border border-harbor-border px-3 py-2.5 text-sm text-harbor-text"
          >
            Actions
          </summary>
          <div className="mt-2 w-48 rounded-lg border border-harbor-border bg-harbor-surface shadow-xl">
            {invitation.email && (
              <>
                <button className={actionClass} disabled={busy} onClick={() => void act('resend')}>
                  Resend invite
                </button>
                <button className={actionClass} disabled={busy} onClick={() => void act('copy')}>
                  Copy invite link
                </button>
              </>
            )}
            <button className={actionClass} disabled={busy} onClick={() => void act('role')}>
              Edit role
            </button>
            <button
              className={`${actionClass} text-red-500`}
              disabled={busy}
              onClick={() => void act('revoke')}
            >
              Revoke
            </button>
          </div>
        </details>
      </td>
    </tr>
  )
}
